// === Admin: Advertisings ===
// Full CRUD page — list, add, edit, hard-delete, active toggle.
// Ported from Click2Gro PHP (AdvertisingsController.php / advertising table).
// Columns: title, client (fertilzer_companies), url (Supabase Storage image), active.
// Note: No soft deletes — original PHP model has useSoftDeletes = false.

import { useEffect, useState, useCallback, useRef, type DragEvent } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Pagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Chip,
  Divider,
  Switch,
  FormControlLabel,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ArrowBack as BackIcon,
  Visibility as ViewIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import {
  AppTableHead,
  AppTableHeadCell,
  AppTableRow,
  DEFAULT_ROWS_PER_PAGE,
  ROWS_PER_PAGE_OPTIONS,
} from '../components/AppTable';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FertilizerCompanyOption {
  readonly id: number;
  readonly company: string;
}

interface AdvertisingRow {
  readonly id: number;
  readonly title: string | null;
  readonly client: number | null;
  readonly client_name: string | null;
  readonly url: string | null;
  readonly url_resolved: string | null; // signed URL from Supabase Storage, or https:// pass-through
  readonly active: number | null;
  readonly created_at: string | null;
  readonly created_by: string | null;
  readonly updated_at: string | null;
  readonly updated_by: string | null;
}

interface FormState {
  title: string;
  client: string;
  url: string;     // raw DB value (storage path or empty)
  viewUrl: string | null; // resolved signed URL for previewing existing image
  active: boolean;
}

const DEFAULT_FORM: FormState = {
  title:   '',
  client:  '',
  url:     '',
  viewUrl: null,
  active:  true,
};

// Valid image extensions accepted for upload
const VALID_IMAGE_EXTS = ['.jpg', '.jpeg', '.png', '.gif', '.webp'] as const;

// Returns true when the url value looks like a storage path (not a full https:// URL)
function isStoredFile(url: string): boolean {
  return !!url && !url.startsWith('http://') && !url.startsWith('https://');
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getApiUrl(): string {
  return (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://localhost:8787';
}

function authHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-AU', { dateStyle: 'medium', timeStyle: 'short' }).format(d);
}

function truncate(str: string | null, max: number): string {
  if (!str) return '—';
  return str.length > max ? `${str.slice(0, max)}…` : str;
}

// ---------------------------------------------------------------------------
// PreviewDialog — shows the ad banner image
// ---------------------------------------------------------------------------

interface PreviewDialogProps {
  readonly row: AdvertisingRow | null;
  readonly onClose: () => void;
}

function PreviewDialog({ row, onClose }: PreviewDialogProps) {
  const imgSrc = row?.url_resolved ?? null;
  return (
    <Dialog open={row !== null} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{row?.title ?? 'Ad Preview'}</span>
        {row?.client_name && (
          <Typography variant="caption" color="text.secondary">
            {row.client_name}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {imgSrc ? (
          <Box
            component="img"
            src={imgSrc}
            alt={row?.title ?? 'Ad banner'}
            sx={{ width: '100%', maxHeight: 300, objectFit: 'contain', borderRadius: 1, bgcolor: 'action.hover' }}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <Box
            sx={{
              height: 150,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'action.hover',
              borderRadius: 1,
            }}
          >
            <Typography color="text.secondary">No image uploaded</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {imgSrc && (
          <Button
            href={imgSrc}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            startIcon={<OpenInNewIcon />}
          >
            Open in new tab
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// AdvertisingsPage
// ---------------------------------------------------------------------------

export function AdvertisingsPage() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const token = session?.access_token ?? '';

  // ---- List state ----
  const [rows, setRows]           = useState<readonly AdvertisingRow[]>([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [search, setSearch]       = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setPage(0); setDebouncedSearch(search); }, 350);
    return () => clearTimeout(t);
  }, [search]);
  const [loading, setLoading]     = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // ---- Fertilizer companies (for client dropdown) ----
  const [companies, setCompanies] = useState<readonly FertilizerCompanyOption[]>([]);

  // ---- Add / Edit dialog ----
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId]   = useState<number | null>(null);
  const [auditInfo, setAuditInfo]   = useState<{
    created_at: string | null; updated_at: string | null;
    created_by: string | null; updated_by: string | null;
  } | null>(null);
  const [form, setForm]             = useState<FormState>(DEFAULT_FORM);
  const [formError, setFormError]   = useState<string | null>(null);
  const [saving, setSaving]         = useState(false);

  // ---- Preview dialog ----
  const [previewRow, setPreviewRow] = useState<AdvertisingRow | null>(null);

  // ---- Delete confirm ----
  const [deleteTarget, setDeleteTarget] = useState<AdvertisingRow | null>(null);
  const [deleting, setDeleting]         = useState(false);

  // ---- Active-toggle in-flight set ----
  const [togglingIds, setTogglingIds] = useState<ReadonlySet<number>>(new Set());

  // ---- File upload ----
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading]           = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [dragOver, setDragOver]             = useState(false);

  // ---- Snackbar ----
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const showSnack = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnack({ open: true, message, severity });

  // ---------------------------------------------------------------------------
  // Upload image to Supabase Storage via API
  // ---------------------------------------------------------------------------
  const uploadImage = async (file: File): Promise<string | null> => {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!VALID_IMAGE_EXTS.some((e) => e === `.${ext}`)) {
      setFormError(`Only image files are accepted (jpg, png, gif, webp). Got: .${ext}`);
      return null;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${getApiUrl()}/api/v1/admin/advertisings/upload`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}` },
        body:    fd,
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `Upload failed (HTTP ${res.status})`);
      }
      const json = (await res.json()) as { path: string };
      setUploadedFileName(file.name);
      return json.path;
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Upload failed');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleFilePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const path = await uploadImage(file);
    if (path) setForm((f) => ({ ...f, url: path, viewUrl: null }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    const path = await uploadImage(file);
    if (path) setForm((f) => ({ ...f, url: path, viewUrl: null }));
  };

  // ---------------------------------------------------------------------------
  // Fetch list
  // ---------------------------------------------------------------------------
  const fetchList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setListError(null);
    try {
      const params = new URLSearchParams({
        page:  String(page + 1),
        limit: String(rowsPerPage),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      });
      const res  = await fetch(`${getApiUrl()}/api/v1/admin/advertisings?${params}`, {
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { data: AdvertisingRow[]; total: number };
      setRows(json.data ?? []);
      setTotal(json.total ?? 0);
    } catch (e) {
      setListError(e instanceof Error ? e.message : 'Failed to load records');
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, debouncedSearch]);

  useEffect(() => { void fetchList(); }, [fetchList]);

  // ---------------------------------------------------------------------------
  // Fetch fertilizer companies when dialog opens
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!dialogOpen || !token || companies.length > 0) return;
    void (async () => {
      try {
        const res = await fetch(
          `${getApiUrl()}/api/v1/admin/fertilizer-companies?page=1&limit=500`,
          { headers: authHeaders(token) },
        );
        if (!res.ok) return;
        const json = (await res.json()) as { data: FertilizerCompanyOption[] };
        setCompanies(
          (json.data ?? [])
            .map((co) => ({ id: co.id, company: co.company ?? '' }))
            .sort((a, b) => a.company.localeCompare(b.company)),
        );
      } catch { /* silent */ }
    })();
  }, [dialogOpen, token, companies.length]);

  // ---------------------------------------------------------------------------
  // Active toggle
  // ---------------------------------------------------------------------------
  const handleActiveToggle = useCallback(async (row: AdvertisingRow) => {
    if (!token) return;
    const newActive = row.active === 1 ? 0 : 1;
    setTogglingIds((prev) => new Set([...prev, row.id]));
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/advertisings/${row.id}/active`, {
        method:  'PATCH',
        headers: authHeaders(token),
        body:    JSON.stringify({ active: newActive }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Optimistic update
      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, active: newActive } : r)),
      );
    } catch {
      showSnack('Failed to update active status', 'error');
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    }
  }, [token]);

  // ---------------------------------------------------------------------------
  // Open Add
  // ---------------------------------------------------------------------------
  const openAdd = () => {
    setEditingId(null);
    setAuditInfo(null);
    setForm(DEFAULT_FORM);
    setUploadedFileName(null);
    setFormError(null);
    setDialogOpen(true);
  };

  // ---------------------------------------------------------------------------
  // Open Edit
  // ---------------------------------------------------------------------------
  const openEdit = async (id: number) => {
    if (!token) return;
    setFormError(null);
    setUploadedFileName(null);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/advertisings/${id}`, {
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error('Failed to load record');
      const json = (await res.json()) as { data: AdvertisingRow };
      const r = json.data;
      setForm({
        title:   r.title  ?? '',
        client:  r.client != null ? String(r.client) : '',
        url:     r.url    ?? '',
        viewUrl: r.url_resolved ?? null,
        active:  r.active === 1,
      });
      if (r.url && isStoredFile(r.url)) {
        setUploadedFileName(r.url.split('/').pop() ?? r.url);
      }
      setAuditInfo({
        created_at:  r.created_at  ?? null,
        updated_at:  r.updated_at  ?? null,
        created_by:  r.created_by  ?? null,
        updated_by:  r.updated_by  ?? null,
      });
      setEditingId(id);
      setDialogOpen(true);
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Failed to load record', 'error');
    }
  };

  // ---------------------------------------------------------------------------
  // Save (create / update)
  // ---------------------------------------------------------------------------
  const handleSave = async () => {
    const title = form.title.trim();
    if (!title) { setFormError('Title is required.'); return; }
    if (title.length > 400) { setFormError('Title must be 400 characters or fewer.'); return; }

    setSaving(true);
    setFormError(null);
    try {
      const url   = editingId
        ? `${getApiUrl()}/api/v1/admin/advertisings/${editingId}`
        : `${getApiUrl()}/api/v1/admin/advertisings`;
      const res = await fetch(url, {
        method:  editingId ? 'PUT' : 'POST',
        headers: authHeaders(token),
        body:    JSON.stringify({
          title,
          client: form.client !== '' ? parseInt(form.client, 10) : null,
          url:    form.url || null,
          active: form.active ? 1 : 0,
        }),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      setDialogOpen(false);
      setUploadedFileName(null);
      showSnack(editingId ? 'Record updated.' : 'Record created.');
      void fetchList();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Delete
  // ---------------------------------------------------------------------------
  const handleDelete = async () => {
    if (!deleteTarget || !token) return;
    setDeleting(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/advertisings/${deleteTarget.id}`, {
        method:  'DELETE',
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDeleteTarget(null);
      showSnack('Record deleted.');
      void fetchList();
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Delete failed', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const pageCount = Math.ceil(total / rowsPerPage);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
        <Tooltip title="Back to dashboard">
          <IconButton onClick={() => navigate('/dashboard')} size="small">
            <BackIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="h5" fontWeight={700} sx={{ flex: 1 }}>
          Advertisings
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
          Add Advertising
        </Button>
      </Box>

      {/* Search + rows-per-page */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search by title…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 280 }}
        />
        <Box sx={{ flex: 1 }} />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Rows</InputLabel>
          <Select
            label="Rows"
            value={rowsPerPage}
            onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
          >
            {ROWS_PER_PAGE_OPTIONS.map((n) => (
              <MenuItem key={n} value={n}>{n}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <AppTableHead>
            <TableRow>
              <AppTableHeadCell>Title</AppTableHeadCell>
              <AppTableHeadCell>Client</AppTableHeadCell>
              <AppTableHeadCell>Image URL</AppTableHeadCell>
              <AppTableHeadCell>Active</AppTableHeadCell>
              <AppTableHeadCell>Created</AppTableHeadCell>
              <AppTableHeadCell align="center">Actions</AppTableHeadCell>
            </TableRow>
          </AppTableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  No advertisings found.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <AppTableRow key={row.id}>
                  <TableCell>{truncate(row.title, 50)}</TableCell>
                  <TableCell>{row.client_name ?? '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.url_resolved ? (
                      <Tooltip title="Click to preview image">
                        <span style={{ cursor: 'pointer', textDecoration: 'underline', color: 'inherit' }}
                          onClick={() => setPreviewRow(row)}>
                          {isStoredFile(row.url ?? '') ? (row.url?.split('/').pop() ?? row.url) : truncate(row.url, 40)}
                        </span>
                      </Tooltip>
                    ) : '—'}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Click to toggle active status">
                      <Chip
                        label={row.active === 1 ? 'Active' : 'Inactive'}
                        color={row.active === 1 ? 'success' : 'default'}
                        size="small"
                        onClick={() => { void handleActiveToggle(row); }}
                        disabled={togglingIds.has(row.id)}
                        sx={{ cursor: 'pointer' }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: 12 }}>
                    {fmtDate(row.created_at)}
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Preview image">
                      <IconButton size="small" onClick={() => setPreviewRow(row)}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => { void openEdit(row.id); }}
                        sx={(theme) => ({
                          color: theme.palette.primary.main,
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
                        })}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => setDeleteTarget(row)}
                        sx={(theme) => ({
                          color: theme.palette.error.main,
                          '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) },
                        })}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </AppTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={pageCount}
            page={page + 1}
            onChange={(_, p) => setPage(p - 1)}
            color="primary"
            size="small"
          />
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        {total} record{total !== 1 ? 's' : ''}
      </Typography>

      {/* ---- Preview Dialog ---- */}
      <PreviewDialog row={previewRow} onClose={() => setPreviewRow(null)} />

      {/* ---- Add / Edit Dialog ---- */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Advertising' : 'Add Advertising'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ pt: 0.5 }}>
            {formError && <Alert severity="error">{formError}</Alert>}

            {/* Title */}
            <TextField
              label="Title"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              inputProps={{ maxLength: 400 }}
              required
              fullWidth
              size="small"
            />

            {/* Client */}
            <FormControl fullWidth size="small">
              <InputLabel id="adv-client-label">Client</InputLabel>
              <Select
                labelId="adv-client-label"
                label="Client"
                value={form.client}
                onChange={(e) => setForm((f) => ({ ...f, client: String(e.target.value) }))}
              >
                <MenuItem value=""><em>— None —</em></MenuItem>
                {companies.map((co) => (
                  <MenuItem key={co.id} value={String(co.id)}>
                    {co.company}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Image upload */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Ad Image (recommended: 1920 × 150 px, JPG or PNG)
              </Typography>
              <Box
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { void handleDrop(e); }}
                onClick={() => fileInputRef.current?.click()}
                sx={(theme) => ({
                  border: `2px dashed ${dragOver ? theme.palette.primary.main : theme.palette.divider}`,
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s',
                  backgroundColor: dragOver
                    ? alpha(theme.palette.primary.main, 0.06)
                    : 'transparent',
                  '&:hover': { borderColor: theme.palette.primary.main },
                })}
              >
                {uploading ? (
                  <CircularProgress size={24} />
                ) : uploadedFileName ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <CheckCircleIcon color="success" />
                    <Typography variant="body2">{uploadedFileName}</Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <CloudUploadIcon color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Drag &amp; drop an image here, or click to select
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      JPG, PNG, GIF, WEBP
                    </Typography>
                  </Box>
                )}
              </Box>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => { void handleFilePick(e); }}
              />
              {uploadedFileName && (
                <Button
                  size="small"
                  sx={{ mt: 0.5 }}
                  onClick={() => {
                    setUploadedFileName(null);
                    setForm((f) => ({ ...f, url: '', viewUrl: null }));
                  }}
                >
                  Remove file
                </Button>
              )}
              {/* View existing stored image (edit mode, no new upload pending) */}
              {form.viewUrl && isStoredFile(form.url) && !uploadedFileName && (
                <Button
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  component="a"
                  href={form.viewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ mt: 0.5 }}
                >
                  View current image
                </Button>
              )}
            </Box>

            {/* Active toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={form.active}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                  color="success"
                />
              }
              label="Active"
            />

            {/* Audit info (edit mode only) */}
            {auditInfo && (
              <>
                <Divider />
                <Stack spacing={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    Created: {fmtDate(auditInfo.created_at)}{auditInfo.created_by ? ` by ${auditInfo.created_by}` : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Updated: {fmtDate(auditInfo.updated_at)}{auditInfo.updated_by ? ` by ${auditInfo.updated_by}` : ''}
                  </Typography>
                </Stack>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => { void handleSave(); }}
            disabled={saving || uploading}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Delete Confirm Dialog ---- */}
      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle>Delete Advertising</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Permanently delete <strong>{deleteTarget?.title ?? `#${deleteTarget?.id}`}</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => { void handleDelete(); }}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Snackbar ---- */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
